import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import dialogActions from 'core/dialogs/actions';
import BottomContent from 'common/BottomContent';
import { PAGES_ROOT } from 'config/api';
import ErrorMsg from 'common/ErrorMsg';
import buttonLinkStyles from 'styles/buttonLink';
import ButtonRowGroup from './common/ButtonRowGroup';

import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    buttonLink: {
      ...buttonLinkStyles(theme),
    },
    categoryType: {
      display: 'none',
    },
    categoryTypes: {
      marginTop: theme.spacing.lg,
    },
    documentList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    documentListItem: {
      display: 'flex',
      fontSize: 14,
      marginBottom: 0,
      paddingBottom: 0,
      width: '100%',
    },
    name: {
      ...theme.typography.p,
      ...theme.typography.p2,
      flex: 1,
    },
    status: {
      flex: 1.5,
      textAlign: 'right',
    },
    topListItem: {
      '&:last-child': {
        border: 'none',
        marginBottom: 0,
        paddingBottom: 0,
      },
      borderBottom: `1px solid ${theme.palette.form.border}`,
      padding: theme.spacing.xs,
    },
  };
};

const DocumentsBare = ({
  bottomContent,
  categoryTypes,
  classes,
  hasFields,
  label,
  labelMobile,
  name,
  openUploadDialog,
  resources,
  subText,
}) => (
  <>
    <legend
      className={classnames('grp-label', classes.label)}
      role={!hasFields ? 'presentation' : undefined}
    >
      <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
        {label}
      </RawHtml>
      <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
        {labelMobile}
      </RawHtml>
    </legend>
    <RawHtml className={classnames('content', classes.content)}>{subText}</RawHtml>

    <ErrorMsg name={name} type="group" />

    <ol className={classnames('catagory-types', classes.categoryTypes)}>
      {categoryTypes.map((type) => (
        <li
          key={`${type.categoryType}-${type.label}`}
          className={classnames('catagory-type', classes.topListItem)}
          data-catagory={type.categoryType}
          data-catagory-label={type.label}
        >
          <RawHtml type="h2" className={classes.categoryType}>
            {type.label}
          </RawHtml>
          <ul className={classnames('document-list', classes.documentList)}>
            {type.documents.map((doc) => (
              <li
                key={`${doc.documentCategory}-${doc.documentName}`}
                data-id={doc.documentId}
                data-status={doc.status}
                className={classes.documentListItem}
              >
                <span className={classnames('name', classes.name)}>
                  <RawHtml>{doc.documentName}</RawHtml>
                </span>
                <span
                  className={classnames('status', classes.status, {
                    'accepted-at': doc.acceptedAt,
                  })}
                >
                  {doc.uploadedAt ? (
                    <>
                      <RawHtml type="span" className="status-label">
                        {doc.status}
                      </RawHtml>{' '}
                      <RawHtml type="span" className="status-datetime">
                        {doc.uploadedAt}
                      </RawHtml>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={classnames('buttonLink btn-upload', classes.buttonLink)}
                      onClick={(e) => {
                        e.preventDefault();
                        openUploadDialog(type.categoryType, type.label);
                      }}
                    >
                      {resources.uploadDocsLinkLabel}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>

    <BottomContent className="bottomContent">{bottomContent}</BottomContent>

    <ButtonRowGroup groupName={name} />
  </>
);

DocumentsBare.propTypes = {
  bottomContent: PropTypes.string,
  categoryTypes: PropTypes.arrayOf(PropTypes.shape({})),
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  hasFields: PropTypes.bool,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  name: PropTypes.string.isRequired,
  openUploadDialog: PropTypes.func.isRequired,
  resources: PropTypes.objectOf(PropTypes.string),
  subText: PropTypes.string,
};

DocumentsBare.defaultProps = {
  bottomContent: '',
  categoryTypes: [],
  hasFields: false,
  label: '',
  labelMobile: '',
  resources: {},
  subText: '',
};

const mapStateToProps = ({ data, groups, resources }, { name }) => {
  const group = groups[name];
  return {
    bottomContent: group.bottomContent,
    categoryTypes: data.categoryTypes || [],
    hasFields: group?.fields?.length > 0 ?? false,
    label: group.label,
    labelMobile: group.labelMobile,
    resources,
    subText: group.subText,
  };
};

const mapDispatchToProps = {
  openUploadDialog: (category, categoryLabel) =>
    dialogActions.open('home_document_upload', {
      category,
      categoryLabel,
      url: `${PAGES_ROOT}/documents`,
    }),
};

const DocumentsStyled = withStyles(styles)(DocumentsBare);

export default connect(mapStateToProps, mapDispatchToProps)(DocumentsStyled);
